import { Suspense, useRef, useState, type MouseEvent, type RefObject } from "react";
import { Outlet } from "react-router";

import { PageSkeleton } from "@/components/common/query-state";
import { useAccountThemeSync } from "@/components/theme/use-account-theme";

import { CommandPalette } from "./command-palette";
import { Sidebar } from "./sidebar";
import { TabBar } from "./tab-bar";
import { TopBar } from "./top-bar";

/** Layout for every signed-in `/app/*` route: sidebar, top bar (the title bar when installed), the phone tab bar,
 * command palette and the page. */
export function AppShell() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  useAccountThemeSync();

  return (
    <div className="min-h-dvh">
      <SkipToContent target={mainRef} />
      <Sidebar />
      <div className="lg:pl-60" data-app-main>
        <TopBar onOpenSearch={() => setPaletteOpen(true)} navOpen={navOpen} onNavOpenChange={setNavOpen} />
        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-[1240px] px-4 pt-6 pb-16 outline-none in-data-[width=full]:max-w-none sm:px-6 sm:pt-8 lg:px-10 lg:pt-9 lg:pb-20"
        >
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <TabBar onMore={() => setNavOpen(true)} />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

function SkipToContent({ target }: { target: RefObject<HTMLElement | null> }) {
  // Focus <main> directly instead of following the hash, which would add "#main-content" to the URL.
  function skip(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    target.current?.focus();
  }

  return (
    <a
      href="#main-content"
      onClick={skip}
      className="fixed top-3 left-3 z-50 -translate-y-16 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-transform focus:translate-y-0"
    >
      Skip to content
    </a>
  );
}
